import { useCallback } from 'react';
import { z, ZodRawShape } from 'zod';
import { IBookingFormDefaultValues, IBookingFormOption, TResourceConfiguration } from '../types/booking-form.types';
import { Option } from '@jooxter/api';
import { BOOKING_OPTION_PREFIX } from '@jooxter/utils';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// The form keys its options by name, not by raw id - see `getDefaultOptions`.
const findBookingOption = (defaultValues?: IBookingFormDefaultValues, id?: number): IBookingFormOption | undefined =>
  id === undefined ? undefined : defaultValues?.options?.[`${BOOKING_OPTION_PREFIX}-${id}`];

const createOptionValueDef = (t: TFunction, option: Option, defaultValue?: IBookingFormOption) => {
  // What the booking already has for this option, falling back to what the resource suggests.
  const value = defaultValue?.value ?? option.defaultValue;

  if (option.mandatory) {
    const mandatoryDef = z
      .string({ required_error: t<string>('booking-form.error.option.value.required') })
      .min(1, t<string>('booking-form.error.option.value.required'));

    return value ? mandatoryDef.default(value) : mandatoryDef;
  }

  return value ? z.string().optional().default(value) : z.string().optional();
};

const createOptionActivatedDef = (t: TFunction, option: Option, defaultValue?: IBookingFormOption) => {
  // Every resource option gets an entry in the form's defaults, activated or not - so it is
  // `activated` that says whether the booking uses it, never the mere presence of the entry. Only
  // when the booking says nothing about the option does the resource's own suggestion apply.
  const isActivated = defaultValue?.activated ?? !!option.selectedByDefault;

  if (option.mandatory) {
    const mandatoryDef = z.literal(true, {
      errorMap: () => ({ message: t<string>('booking-form.error.option.activated.required') }),
    });

    return isActivated ? mandatoryDef.default(true) : mandatoryDef;
  }

  return z.boolean().default(isActivated);
};

export const useCreateOptionsDef = (config?: TResourceConfiguration, defaultValues?: IBookingFormDefaultValues) => {
  const { t } = useTranslation();

  const createOptionsDef = useCallback((): null | z.ZodObject<ZodRawShape> => {
    const resourceOptions = config?.options;

    if (!resourceOptions?.length) {
      return null;
    }

    const optionsSchema = resourceOptions.map((resourceOption: Option) => {
      const bookingOption = findBookingOption(defaultValues, resourceOption.id);
      const optionDef = z.object({
        activated: createOptionActivatedDef(t, resourceOption, bookingOption),
        value: createOptionValueDef(t, resourceOption, bookingOption),
      });

      return [
        `${BOOKING_OPTION_PREFIX}-${resourceOption.id}`,
        resourceOption.mandatory ? optionDef : optionDef.optional(),
      ];
    });

    return z.object(Object.fromEntries(optionsSchema));
  }, [config, defaultValues, t]);

  return { createOptionsDef };
};
